import { httpError } from '#app/api/errors'

export type ConsentClient = {
    client_id: string
    client_name: string
    client_uri: string
    logo_uri: string
}

export type ConsentMissingIdp = {
    name: string
    href: string
}

export type ConsentAssociation = {
    organisationId: string
    organisationName: string
    type: string
    selectable: boolean
    // False when the association's login profiles are not satisfied by the
    // IdPs this session authenticated with — a grant could never activate it.
    // missingIdps carry ready-made login links (session augmentation) that
    // return to this consent page afterwards. Both fields are absent on
    // older fixrate-security versions.
    activatable?: boolean
    missingIdps?: ConsentMissingIdp[]
}

export type ConsentDetails = {
    client: ConsentClient
    resource: string
    supported_grant_ttls: string[]
    associations: ConsentAssociation[]
}

const AUTHORIZE_PARAM_KEYS = [
    'response_type',
    'client_id',
    'redirect_uri',
    'state',
    'resource',
    'code_challenge',
    'code_challenge_method',
] as const

type AuthorizeParamKey = (typeof AUTHORIZE_PARAM_KEYS)[number]

export function readAuthorizeParams(query: URLSearchParams): Record<AuthorizeParamKey, string> {
    const out: Partial<Record<AuthorizeParamKey, string>> = {}
    for (const key of AUTHORIZE_PARAM_KEYS) {
        out[key] = query.get(key) ?? ''
    }
    return out as Record<AuthorizeParamKey, string>
}

export async function fetchConsentDetails(params: Record<AuthorizeParamKey, string>): Promise<ConsentDetails> {
    const qs = new URLSearchParams(params)
    const response = await fetch(`/oauth/consent?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
    })
    if (!response.ok) {
        throw await httpError(response, 'GET /oauth/consent')
    }
    return (await response.json()) as ConsentDetails
}

export async function submitConsent(input: {
    params: Record<AuthorizeParamKey, string>
    approved: boolean
    grantTtl?: string
    allowedOrganisationIds?: string[]
}): Promise<{ redirect_url: string }> {
    const body = {
        ...input.params,
        approved: input.approved,
        grant_ttl: input.grantTtl,
        allowed_organisation_ids: input.allowedOrganisationIds,
    }
    const response = await fetch('/oauth/consent', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    })
    if (!response.ok) {
        throw await httpError(response, 'POST /oauth/consent')
    }
    return (await response.json()) as { redirect_url: string }
}
